<template>
  <div class="info-table-section">
    <!-- タイトル・アクション表示 -->
    <div v-if="title || $slots.headerActions" class="section-header d-flex justify-content-between align-items-center mb-3">
      <ContentText
        v-if="title"
        tag="h3"
        size="lg"
        color="default"
        class="section-title mb-0"
      >
        {{ title }}
      </ContentText>
      <div v-if="$slots.headerActions" class="header-actions">
        <slot name="headerActions" />
      </div>
    </div>

    <!-- ローディング表示 -->
    <div v-if="loading" class="loading-state text-center py-5">
      <BSpinner variant="primary" label="読み込み中..." />
      <p class="mt-2 text-muted">データを読み込んでいます...</p>
    </div>

    <!-- エラー表示 -->
    <BAlert v-else-if="error" variant="danger" show class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
        </div>
        <BButton
          variant="outline-danger"
          size="sm"
          @click="handleRefresh"
        >
          <i class="bi bi-arrow-clockwise me-1"></i>更新
        </BButton>
      </div>
    </BAlert>

    <!-- 空データ表示 -->
    <div v-else-if="displayItems.length === 0">
      <!-- 空データの場合もヘッダーアクションを表示 -->
      <div v-if="$slots.headerActions && !title" class="header-actions-only d-flex justify-content-end mb-3">
        <slot name="headerActions" />
      </div>

      <div class="empty-state text-center py-5">
        <div class="empty-icon mb-3">
          <i class="bi bi-inbox text-muted" style="font-size: 3rem;"></i>
        </div>
        <div class="empty-content">
          <h5 class="text-muted mb-2">データがありません</h5>
          <p class="text-muted mb-0" style="max-width: 400px; margin: 0 auto; line-height: 1.5;">
            {{ emptyMessage }}
          </p>
        </div>
      </div>
    </div>

    <!-- テーブル表示 -->
    <div v-else>
      <BTableSimple responsive hover class="location-table">
      <BThead class="table-header">
        <BTr>
          <BTh
            v-for="(header, index) in headers"
            :key="index"
            class="text-center"
          >
            {{ header }}
          </BTh>
        </BTr>
      </BThead>
      <BTbody>
        <BTr v-for="item in paginatedItems" :key="item.id">
          <!-- カスタムテーブル行スロットが提供された場合はそれを使用 -->
          <template v-if="$slots.tableRow">
            <slot name="tableRow" :item="item" :handleAction="handleAction" />
          </template>
          <!-- デフォルトの位置情報用テーブル行（後方互換性のため保持） -->
          <template v-else>
            <BTd class="facility-name">
              <ContentText color="default">
                {{ item.name }}
              </ContentText>
            </BTd>
            <BTd class="location-info">
              <ContentText color="muted">
                {{ formatLocationInfo(item) }}
              </ContentText>
            </BTd>
            <BTd class="actions-cell">
              <div class="action-buttons">
                <BButton
                  v-for="action in actionButtons"
                  :key="action.action"
                  :variant="action.variant"
                  size="sm"
                  @click="handleAction(action.action, item)"
                  :class="`${action.action}-btn`"
                >
                  {{ action.label }}
                </BButton>
              </div>
            </BTd>
          </template>
        </BTr>
      </BTbody>
    </BTableSimple>

    <!-- ページネーション -->
    <BPagination
      v-if="pagination.enabled && displayItems.length > pagination.perPage"
      v-model="currentPage"
      :total-rows="displayItems.length"
      :per-page="pagination.perPage"
      class="justify-content-center mt-3"
    />
    </div>
  </div>
</template><script setup>
import { ref, computed, watch } from 'vue'
import ContentText from '@/components/atoms/ContentText.vue'
import { BSpinner, BAlert, BPagination } from 'bootstrap-vue-next'

/**
 * InfoTableSection - 情報テーブルセクションコンポーネント (Organism)
 *
 * Bootstrap Vueのb-table-simpleを使用した汎用的な情報表示用のOrganismコンポーネントです。
 * テーブルヘッダー、データ表示項目、操作ボタンを設定可能な形で提供します。
 *
 * 2025年11月拡張:
 * - ローディング・エラー・空データ表示機能
 * - ページネーション機能
 * - 汎用化（items props追加、後方互換性維持）
 *
 * アトミックデザイン構造:
 * - Organism: InfoTableSection (このコンポーネント)
 *   └── Atoms: ContentText
 *   └── Bootstrap Vue: b-table-simple, b-button, b-spinner, b-alert, b-pagination
 *
 * 責務:
 * - 汎用的な情報テーブルの表示
 * - 設定可能な操作ボタンの提供
 * - アクションイベントの親コンポーネントへの伝達
 * - Bootstrap Vueを活用したレスポンシブテーブル
 * - 統一されたスタイリングの適用
 * - 状態管理（ローディング、エラー、空データ）
 * - ページネーション制御
 */

const props = defineProps({
  /**
   * セクションのタイトル
   */
  title: {
    type: String,
    required: true
  },
  /**
   * テーブルヘッダーの配列
   * 各要素は表示するヘッダー名の文字列
   */
  headers: {
    type: Array,
    required: true,
    validator: (headers) => {
      return headers.every(header => typeof header === 'string')
    }
  },
  /**
   * 操作ボタンの設定配列
   * 各要素は { label, variant, action } の形式
   */
  actionButtons: {
    type: Array,
    default: () => [
      { label: '編集', variant: 'success', action: 'edit' },
      { label: 'クリア', variant: 'danger', action: 'clear' }
    ],
    validator: (actions) => {
      return actions.every(action =>
        action &&
        typeof action.label === 'string' &&
        typeof action.variant === 'string' &&
        typeof action.action === 'string'
      )
    }
  },
  /**
   * 情報データの配列（汎用化）
   * 各要素は { id, name } を最低限含むオブジェクト
   */
  items: {
    type: Array,
    default: () => [],
    validator: (items) => {
      return items.every(item =>
        item &&
        typeof item.id !== 'undefined'
      )
    }
  },
  /**
   * 情報データの配列（後方互換性のため保持）
   * itemsが指定されていない場合に使用
   */
  locations: {
    type: Array,
    default: () => [],
    validator: (locations) => {
      return locations.every(location =>
        location &&
        typeof location.id !== 'undefined'
      )
    }
  },
  /**
   * ローディング状態
   */
  loading: {
    type: Boolean,
    default: false
  },
  /**
   * エラーメッセージ
   */
  error: {
    type: String,
    default: null
  },
  /**
   * 空データ時の表示メッセージ
   */
  emptyMessage: {
    type: String,
    default: 'データがありません'
  },
  /**
   * ページネーション設定
   */
  pagination: {
    type: Object,
    default: () => ({ enabled: false, perPage: 10 }),
    validator: (pagination) => {
      return typeof pagination.enabled === 'boolean' &&
             typeof pagination.perPage === 'number' &&
             pagination.perPage > 0
    }
  }
})

const emit = defineEmits(['action', 'refresh', 'edit', 'clear'])

const currentPage = ref(1)

/**
 * 表示用データ配列（items優先、後方互換性でlocationsも対応）
 */
const displayItems = computed(() => {
  return props.items.length > 0 ? props.items : props.locations
})

/**
 * ページネーション適用後のデータ
 */
const paginatedItems = computed(() => {
  if (!props.pagination.enabled) {
    return displayItems.value
  }

  const start = (currentPage.value - 1) * props.pagination.perPage
  const end = start + props.pagination.perPage
  return displayItems.value.slice(start, end)
})

/**
 * データが変更された際にページを1に戻す
 */
watch(displayItems, () => {
  currentPage.value = 1
})

/**
 * 位置情報をフォーマットして表示用文字列を生成
 * @param {Object} location - 施設位置情報オブジェクト
 * @returns {string} フォーマットされた位置情報文字列
 */
const formatLocationInfo = (location) => {
  const { latitude, longitude } = location

  // どちらかがnullまたは未定義の場合は空欄
  if (latitude == null || longitude == null) {
    return ''
  }

  // 両方とも値がある場合はカンマ区切りで表示（小数点以下6桁）
  const formattedLatitude = Number(latitude).toFixed(6)
  const formattedLongitude = Number(longitude).toFixed(6)
  return `${formattedLatitude}, ${formattedLongitude}`
}

/**
 * アクションボタンクリック時の汎用ハンドラー
 * @param {string} action - 実行するアクション名
 * @param {Object} location - 対象の情報オブジェクト
 */
const handleAction = (action, location) => {
  // 新しい汎用イベントを発火
  /**
   * @event action
   * @type {Object} { action: string, data: Object }
   */
  emit('action', { action, data: location })

  // 後方互換性のため、既存のイベントも発火
  if (action === 'edit') {
    handleEdit(location)
  } else if (action === 'clear') {
    handleClear(location)
  }
}

/**
 * 編集ボタンクリック時のハンドラー（後方互換性のため残す）
 * @param {Object} location - 対象の施設位置情報オブジェクト
 */
const handleEdit = (location) => {
  /**
   * @event edit
   * @type {Object} 施設位置情報オブジェクト
   */
  emit('edit', location)
}

/**
 * クリアボタンクリック時のハンドラー（後方互換性のため残す）
 * @param {Object} location - 対象の施設位置情報オブジェクト
 */
const handleClear = (location) => {
  /**
   * @event clear
   * @type {Object} 施設位置情報オブジェクト
   */
  emit('clear', location)
}

/**
 * 更新ボタンクリック時のハンドラー
 */
const handleRefresh = () => {
  /**
   * @event refresh
   * @type {void}
   */
  emit('refresh')
}
</script>

<style lang="scss" scoped>
.info-table-section {
  margin-bottom: 2rem;
  margin-left: 2rem;
  margin-right: 2rem;
}

.section-title {
  margin-bottom: 1.5rem;
  margin-top: 0;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.025em;
}

.location-table {
  background: #ffffff;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  max-width: 900px;
  width: 100%;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
}

// テーブルヘッダーのスタイリング
.table-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e5e7eb;

  th {
    font-weight: 600;
    color: #374151;
    padding: 1.25rem 1rem;
    border-bottom: none;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: left;
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background: rgba(0, 0, 0, 0.1);
    }
  }
}

// テーブルボディのスタイリング
tbody {
  td {
    padding: 1.25rem 1rem;
    vertical-align: middle;
    border-bottom: 1px solid #f3f4f6;
    transition: background-color 0.2s ease;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr {
    transition: all 0.2s ease;

    &:hover {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      transform: translateX(2px);
      box-shadow: inset 4px 0 0 #10b981;
    }
  }
}

// 各列のスタイリング
.facility-name {
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.location-info {
  font-family: 'JetBrains Mono', 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  color: #6b7280;
  background-color: #f9fafb;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  display: inline-block;
}

.actions-cell {
  min-width: 160px;
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  justify-content: center;

  .edit-btn,
  .clear-btn {
    min-width: 70px;
    font-weight: 500;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    font-size: 0.875rem;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .edit-btn {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: #059669;

    &:hover {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      border-color: #047857;
    }
  }

  .clear-btn {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border-color: #dc2626;

    &:hover {
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
      border-color: #b91c1c;
    }
  }
}

// ローディング状態のスタイリング
.loading-state {
  background: #ffffff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  padding: 2rem;

  .text-muted {
    color: #6b7280 !important;
    font-size: 0.875rem;
  }
}

// 空データ状態のスタイリング
.empty-state {
  background: #ffffff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  padding: 3rem 2rem;

  i {
    opacity: 0.5;
    margin-bottom: 1rem;
  }

  .text-muted {
    color: #6b7280 !important;
    font-size: 0.95rem;
  }
}

// アラート（エラー表示）のカスタマイズ
.alert-danger {
  border-radius: 0.75rem;
  border: 1px solid #f87171;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);

  i {
    color: #dc2626;
  }
}

// レスポンシブ対応
@media (max-width: 768px) {
  .info-table-section {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  .location-table {
    font-size: 0.875rem;
    max-width: 100%;
    border-radius: 0.75rem;
  }

  .table-header th,
  tbody td {
    padding: 1rem 0.75rem;
  }

  .section-title {
    font-size: 1.375rem;
    margin-bottom: 1.25rem;
  }

  .location-info {
    font-size: 0.8rem;
    padding: 0.375rem 0.5rem;
  }

  .actions-cell {
    min-width: 140px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.5rem;

    .edit-btn,
    .clear-btn {
      min-width: auto;
      width: 100%;
      padding: 0.625rem 1rem;
    }
  }
}

@media (max-width: 480px) {
  .info-table-section {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }

  .location-table {
    border-radius: 0.5rem;
  }

  .section-title {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  .location-info {
    font-size: 0.75rem;
    padding: 0.25rem 0.375rem;
  }

  .table-header th {
    font-size: 0.8rem;
    padding: 1rem 0.5rem;
  }

  tbody td {
    padding: 1rem 0.5rem;
  }
}
</style>
